import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSupabase, useTenant } from "@how/app-kit";
import { DomainError, type DomainRecord, type DomainStatus } from "../website/domain-queries";

/**
 * A church's own sign-in addresses (ADR 0067 phase B): `dash.<domain>` for the staff dashboard and `members.<domain>`
 * for the member portal. The database decides everything (owner only, two-step every time, never in a support
 * session); this module reads the screen's data, sends the owner's changes, and words the answers.
 */

export type SignInSurface = "staff_dashboard" | "member_portal";
export type PreferredAddress = "how" | "church";

export const SIGN_IN_SURFACES: readonly SignInSurface[] = ["staff_dashboard", "member_portal"];

/** Who signs in at each app's address, as the screen says it. */
export const WHO: Readonly<Record<SignInSurface, "Staff" | "Members">> = {
  staff_dashboard: "Staff",
  member_portal: "Members",
};

export interface SignInAddress {
  readonly id: string;
  readonly surface: SignInSurface;
  readonly hostname: string;
  readonly status: DomainStatus;
  readonly lastError: string | null;
  readonly liveSince: string | null;
  readonly lastCheckedAt: string | null;
  /** The church's domain, without the first label (`mychurch.org`). */
  readonly domain: string;
  readonly records: readonly DomainRecord[];
}

/** Everything the page shows (`sign_in_domain_setup`). */
export interface SignInSetup {
  /** Where every CNAME points: `sites.<platform domain>`. */
  readonly target: string;
  /** HoW's own addresses, which always work: `dash.<base>/<slug>` and `<slug>.members.<base>`. */
  readonly platformAddresses: { readonly staff: string; readonly members: string };
  /** The website's live custom domain without its first label, to suggest. */
  readonly websiteDomain: string | null;
  readonly preferred: PreferredAddress;
  readonly addresses: readonly SignInAddress[];
}

function useTenantId(): string | null {
  const tenant = useTenant();
  return tenant.status === "resolved" ? tenant.tenant.tenantId : null;
}

function failed(error: { message: string; code?: string } | null): void {
  if (error !== null) throw new DomainError(error.message, error.code);
}

/** True while an address is on its way to live, when the page keeps itself up to date. */
export function isSettling(address: SignInAddress): boolean {
  return address.status === "pending_verification" || address.status === "verifying";
}

export function useSignInSetup() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["sign-in-setup", tenantId],
    enabled: tenantId !== null,
    queryFn: async (): Promise<SignInSetup> => {
      const { data, error } = await supabase.rpc("sign_in_domain_setup", {
        p_tenant_id: tenantId ?? "",
      });
      failed(error);
      return data as unknown as SignInSetup;
    },
    // HoW checks a waiting address on its own, usually within a few minutes; the page follows without anyone
    // pressing anything.
    refetchInterval: (query) =>
      query.state.data?.addresses.some(isSettling) === true ? 15_000 : false,
  });
}

function useRefresh() {
  const client = useQueryClient();
  const tenantId = useTenantId();
  return () => client.invalidateQueries({ queryKey: ["sign-in-setup", tenantId] });
}

export function useRequestSignInAddress() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const refresh = useRefresh();
  return useMutation({
    mutationFn: async (input: {
      readonly surface: SignInSurface;
      readonly domain: string;
    }): Promise<void> => {
      const { error } = await supabase.rpc("request_sign_in_domain", {
        p_tenant_id: tenantId ?? "",
        p_surface: input.surface,
        p_domain: input.domain,
      });
      failed(error);
    },
    onSuccess: refresh,
  });
}

export function useRecheckSignInAddress() {
  const supabase = useSupabase();
  const refresh = useRefresh();
  return useMutation({
    mutationFn: async (domainId: string): Promise<void> => {
      const { error } = await supabase.rpc("recheck_custom_domain", { p_domain_id: domainId });
      failed(error);
    },
    onSuccess: refresh,
  });
}

export function useRemoveSignInAddress() {
  const supabase = useSupabase();
  const refresh = useRefresh();
  return useMutation({
    mutationFn: async (domainId: string): Promise<void> => {
      const { error } = await supabase.rpc("remove_tenant_domain", { p_domain_id: domainId });
      failed(error);
    },
    onSuccess: refresh,
  });
}

export function useSetPreferredAddress() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const refresh = useRefresh();
  return useMutation({
    mutationFn: async (preferred: PreferredAddress): Promise<void> => {
      const { error } = await supabase.rpc("set_preferred_sign_in_address", {
        p_tenant_id: tenantId ?? "",
        p_preferred: preferred,
      });
      failed(error);
    },
    onSuccess: refresh,
  });
}

const HOSTNAME = /^([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;
const LEADING_LABEL = /^(www|dash|members)\./;

/**
 * What the owner typed, as the database will keep it (ADR 0067 P5): no scheme, no path, lower case, and no leading
 * www., dash. or members., however many there are. The app adds the label.
 */
export function tidyChurchDomain(input: string): string {
  let domain = input
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/[/?#].*$/, "");
  while (LEADING_LABEL.test(domain)) domain = domain.replace(LEADING_LABEL, "");
  return domain;
}

/** Why the typed domain cannot be sent, or null when it can. The database checks all of it again. */
export function churchDomainProblem(input: string): string | null {
  const domain = tidyChurchDomain(input);
  // "members." is the longer label; the whole address stays within 253 characters.
  if (!HOSTNAME.test(domain) || domain.length > 245) {
    return "Enter your church's domain, such as yourchurch.org.";
  }
  if (domain === "dcxlogic.com" || domain.endsWith(".dcxlogic.com")) {
    return "That address is one of HoW's own. Enter your church's domain.";
  }
  return null;
}

/** The address an app gets on the church's domain. */
export function signInHost(surface: SignInSurface, domain: string): string {
  return `${surface === "staff_dashboard" ? "dash" : "members"}.${domain}`;
}

/** The apps that have no address yet (one each, ADR 0067 R5), staff first. */
export function freeSurfaces(addresses: readonly SignInAddress[]): SignInSurface[] {
  return SIGN_IN_SURFACES.filter((s) => !addresses.some((a) => a.surface === s));
}

export function hasLiveAddress(addresses: readonly SignInAddress[]): boolean {
  return addresses.some((a) => a.status === "active");
}

/** While "Our own address" is chosen, each address that is not live is one HoW's links skip (spec section 9). */
export function pausedLines(setup: Pick<SignInSetup, "preferred" | "addresses">): string[] {
  if (setup.preferred !== "church") return [];
  return setup.addresses
    .filter((a) => a.status !== "active")
    .map((a) => `Paused: ${a.hostname} isn't live, so HoW's links use HoW's address.`);
}

/** A DCX Logic support session never changes or checks a church's sign-in addresses (ADR 0067 P3, S3). */
export const SUPPORT_SESSION_SENTENCE =
  "Sign-in addresses cannot be changed or checked during a DCX Logic support session. Your church's owner does this, signed in as themselves.";

function sentence(message: string): string {
  return message.charAt(0).toUpperCase() + message.slice(1) + ".";
}

/** What a refused call means to the owner who made it. */
export function signInErrorMessage(error: unknown): string {
  const code = error instanceof DomainError ? error.code : undefined;
  const message = error instanceof DomainError ? error.message : "";
  if (code === "42501") {
    if (/support session/.test(message)) return SUPPORT_SESSION_SENTENCE;
    if (/two-step/.test(message)) return "Set up two-step verification first, then try again.";
    return "Only your church's owner can change sign-in addresses.";
  }
  if (code === "23505") {
    // "Your church already has a staff sign-in address; remove it first" is the database's own sentence.
    return /already has/.test(message)
      ? sentence(message)
      : "That address is already connected to HoW.";
  }
  // The database's sentences for a broken rule are written for the person.
  if (code === "23514") return sentence(message);
  if (code === "P0002") return "That address is no longer here. Refresh the page.";
  return "Something went wrong. Try again.";
}
